/**
 * Test Step Card Component
 *
 * Dynamically renders test step questions based on question_types JSONB from database.
 * Supports yes/no, dropdown, checkbox, and combined question types.
 *
 * Module: 3.0 - System Operations (Issues Manager Phase 2)
 * PRD: docs/prds/PRD-ISSUES-MANAGER.md v2.1
 */

import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, CheckCircle2, Camera, Code, Bot, User, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { determineOverallResult, TEST_RESULT_COLORS } from "@/lib/constants/testing-constants";
import { cn } from "@/lib/utils";
import type { TestingChecklistItem, TestStepExecution } from "@shared/schema";

interface TestStepCardProps {
  item: TestingChecklistItem;
  execution?: TestStepExecution;  // Previous execution if exists
  linkedIssues?: any[];  // Issues linked to this test step
  onExecute: (itemId: string, data: {
    result: string;
    responseData: Record<string, any>;
    notes: string;
  }) => Promise<void>;
  onReportIssue: (item: TestingChecklistItem) => void;
}

export function TestStepCard({
  item,
  execution,
  linkedIssues = [],
  onExecute,
  onReportIssue,
}: TestStepCardProps) {
  const { toast } = useToast();

  // Get test type and source info (with fallback for existing items)
  const testType = (item as any).testType || "manual";
  const testFilePath = (item as any).testFilePath;
  const testLineNumber = (item as any).testLineNumber;
  const testReportPath = (item as any).testReportPath;

  // Helper to copy source path to clipboard
  const handleViewSource = () => {
    const sourcePath = testLineNumber
      ? `${testFilePath}:${testLineNumber}`
      : testFilePath;
    navigator.clipboard.writeText(sourcePath);
    toast({
      title: "Copied to clipboard",
      description: sourcePath,
    });
  };

  // Helper to open test report in new tab
  const handleViewReport = () => {
    if (testReportPath) {
      // Open the archived report index.html in a new tab
      const reportUrl = `/${testReportPath}/index.html`;
      window.open(reportUrl, '_blank');
    }
  };

  // Helper to parse responseData (handles JSONB object, legacy string, or null)
  const parseResponseData = (data: any): Record<string, any> => {
    if (!data) return {};
    if (typeof data === 'object') return data; // JSONB returns as object
    if (typeof data === 'string') {
      try {
        return JSON.parse(data); // Legacy string format
      } catch (e) {
        console.error('Failed to parse responseData:', e);
        return {};
      }
    }
    return {};
  };

  // Current state (being edited)
  const [responses, setResponses] = useState<Record<string, any>>(
    parseResponseData(execution?.responseData)
  );
  const [notes, setNotes] = useState(execution?.notes || "");

  // Saved state (last successfully saved to database)
  const [savedResponses, setSavedResponses] = useState<Record<string, any>>(
    parseResponseData(execution?.responseData)
  );
  const [savedNotes, setSavedNotes] = useState(execution?.notes || "");

  const [isSaving, setIsSaving] = useState(false);

  // Sync state with execution prop when it changes (e.g., after navigation back to checklist)
  useEffect(() => {
    if (execution) {
      // Parse responseData (might be JSON string from backend)
      const executionResponses = parseResponseData(execution.responseData);
      const executionNotes = execution.notes || "";

      setResponses(executionResponses);
      setNotes(executionNotes);
      setSavedResponses(executionResponses);
      setSavedNotes(executionNotes);
    }
  }, [execution]);

  // Detect if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(responses) !== JSON.stringify(savedResponses) ||
           notes !== savedNotes;
  }, [responses, notes, savedResponses, savedNotes]);

  // Parse question_types from JSONB (can be single object or array)
  const questionTypes = item.questionTypes;

  /**
   * Renders a single question based on type.
   */
  const renderQuestion = (config: any, responseKey: string) => {
    const qType = config.type;

    // Yes/No Radio Buttons
    if (qType === "yes_no") {
      return (
        <div className="space-y-2">
          <Label>{config.label}</Label>
          <RadioGroup
            value={responses[responseKey]?.answer}
            onValueChange={(val) => {
              setResponses({
                ...responses,
                [responseKey]: { answer: val },
              });
            }}
          >
            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id={`${responseKey}-yes`} />
                <Label htmlFor={`${responseKey}-yes`}>✅ Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id={`${responseKey}-no`} />
                <Label htmlFor={`${responseKey}-no`}>❌ No</Label>
              </div>
            </div>
          </RadioGroup>
        </div>
      );
    }

    // Dropdown Select
    if (qType === "dropdown") {
      return (
        <div className="space-y-2">
          <Label>{config.label}</Label>
          <Select
            value={responses[responseKey]?.selected}
            onValueChange={(val) => {
              const index = config.options.indexOf(val);
              setResponses({
                ...responses,
                [responseKey]: { selected: val, index },
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${config.label}`} />
            </SelectTrigger>
            <SelectContent>
              {config.options.map((opt: string) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Checkbox Multi-Select
    if (qType === "checkbox") {
      const selected = responses[responseKey]?.selected || [];
      return (
        <div className="space-y-2">
          <Label>{config.label}</Label>
          <div className="space-y-2">
            {config.options.map((opt: string) => (
              <div key={opt} className="flex items-center space-x-2">
                <Checkbox
                  id={`${responseKey}-${opt}`}
                  checked={selected.includes(opt)}
                  onCheckedChange={(checked) => {
                    const newSelected = checked
                      ? [...selected, opt]
                      : selected.filter((x: string) => x !== opt);
                    const unselected = config.options.filter(
                      (o: string) => !newSelected.includes(o)
                    );
                    setResponses({
                      ...responses,
                      [responseKey]: { selected: newSelected, unselected },
                    });
                  }}
                />
                <Label htmlFor={`${responseKey}-${opt}`}>{opt}</Label>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Pass/Fail Buttons (Sprint 3 format)
    if (qType === "pass_fail") {
      return (
        <div className="space-y-2">
          <Label>{config.label || "Test Result"}</Label>
          <div className="flex gap-4">
            <Button
              type="button"
              variant={responses[responseKey]?.answer === "pass" ? "default" : "outline"}
              className={cn(
                "flex-1",
                responses[responseKey]?.answer === "pass" && "bg-green-600 hover:bg-green-700"
              )}
              onClick={() => {
                setResponses({
                  ...responses,
                  [responseKey]: { answer: "pass" },
                });
              }}
            >
              ✅ Pass
            </Button>
            <Button
              type="button"
              variant={responses[responseKey]?.answer === "fail" ? "default" : "outline"}
              className={cn(
                "flex-1",
                responses[responseKey]?.answer === "fail" && "bg-red-600 hover:bg-red-700"
              )}
              onClick={() => {
                setResponses({
                  ...responses,
                  [responseKey]: { answer: "fail" },
                });
              }}
            >
              ❌ Fail
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  /**
   * Handles test execution submission.
   */
  const handleExecute = async () => {
    setIsSaving(true);
    try {
      const result = determineOverallResult(responses, questionTypes);
      await onExecute(item.id, {
        result,
        responseData: responses,
        notes,
      });

      // Update saved state after successful save
      setSavedResponses(responses);
      setSavedNotes(notes);
    } finally {
      setIsSaving(false);
    }
  };

  // Determine if questions are combined (array) or single
  const isCombined = Array.isArray(questionTypes);

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-semibold">
                {item.testId} Step {item.stepNumber || ""}: {item.testDescription}
              </h3>
              {/* Test Type Badge */}
              <Badge
                variant={testType === "automated" ? "secondary" : "outline"}
                className={cn(
                  "shrink-0",
                  testType === "automated"
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                )}
              >
                {testType === "automated" ? (
                  <>
                    <Bot className="h-3 w-3 mr-1" />
                    Automated
                  </>
                ) : (
                  <>
                    <User className="h-3 w-3 mr-1" />
                    Manual
                  </>
                )}
              </Badge>
              {/* Result Badge */}
              {execution && execution.result && (
                <Badge
                  variant="outline"
                  className={cn("shrink-0", TEST_RESULT_COLORS[execution.result])}
                >
                  {execution.result.toUpperCase()}
                </Badge>
              )}
              {/* View Source Button (for automated tests) */}
              {testType === "automated" && testFilePath && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  onClick={handleViewSource}
                >
                  <Code className="h-3 w-3 mr-1" />
                  View Source
                </Button>
              )}
              {/* View Report Button (when report path exists) */}
              {testReportPath && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  onClick={handleViewReport}
                >
                  <FileText className="h-3 w-3 mr-1" />
                  View Report
                </Button>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              <strong>Expected:</strong> {item.expectedResult}
            </p>
            {item.preconditions && (
              <p className="text-sm text-muted-foreground">
                <strong>Preconditions:</strong> {item.preconditions}
              </p>
            )}
            {/* Source Path Display (for automated tests) */}
            {testType === "automated" && testFilePath && (
              <p className="text-xs text-muted-foreground font-mono">
                <Code className="h-3 w-3 inline mr-1" />
                {testFilePath}{testLineNumber ? `:${testLineNumber}` : ""}
              </p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Dynamic Question Rendering */}
        {isCombined ? (
          // Render combined questions
          (questionTypes as any[]).map((q: any, idx: number) => (
            <div key={idx}>{renderQuestion(q, `q${idx}`)}</div>
          ))
        ) : (
          // Render single question
          <div>{renderQuestion(questionTypes, (questionTypes as any).type)}</div>
        )}

        {/* Notes Section */}
        <div className="space-y-2">
          <Label htmlFor={`notes-${item.id}`}>Notes</Label>
          <Textarea
            id={`notes-${item.id}`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any observations or notes..."
            className="min-h-[80px]"
          />
        </div>

        {/* Auto-Generated Notes Display (if exists from checkbox execution) */}
        {execution?.autoGeneratedNotes && (
          <div className="rounded-md bg-muted p-3 space-y-1">
            <p className="text-sm font-medium">Auto-Generated Notes:</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {execution.autoGeneratedNotes}
            </p>
          </div>
        )}

        {/* Linked Issues Display */}
        {linkedIssues.length > 0 && (
          <div className="rounded-md border bg-muted/50 p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span>Linked Issues ({linkedIssues.length})</span>
            </div>
            <div className="space-y-1">
              {linkedIssues.map((issue) => (
                <div key={issue.id} className="flex items-center gap-2 text-sm">
                  <Badge
                    variant={
                      issue.severity === "critical"
                        ? "destructive"
                        : issue.severity === "high"
                        ? "default"
                        : "secondary"
                    }
                    className="text-xs"
                  >
                    {issue.severity}
                  </Badge>
                  <a
                    href={`/issues/${issue.id}`}
                    className="text-primary hover:underline flex-1"
                  >
                    {issue.issueNumber}: {issue.title}
                  </a>
                  <Badge variant="outline" className="text-xs">
                    {issue.status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legacy Linked Issue Display (from execution) */}
        {execution?.issueId && (
          <div className="flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span>
              Linked to issue:{" "}
              <a
                href={`/issues/${execution.issueId}`}
                className="text-primary hover:underline"
              >
                {execution.issueId}
              </a>
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            size="sm"
            onClick={handleExecute}
            disabled={isSaving || !hasUnsavedChanges}
            variant={!hasUnsavedChanges ? "outline" : "default"}
            className="gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            {isSaving ? "Saving..." : hasUnsavedChanges ? "Save Result" : "Saved"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReportIssue(item)}
            className="gap-2"
          >
            <AlertCircle className="h-4 w-4" />
            Report Issue
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="gap-2"
            onClick={() => {
              // TODO: Implement screenshot capture (Phase 3)
              console.log("Screenshot capture not yet implemented");
            }}
          >
            <Camera className="h-4 w-4" />
            Screenshot
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
